import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Trophy, FileText, Globe, MapPin, Calendar, Clock, ExternalLink, Flag, Download, Award, Users, ArrowRight, Medal } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

// Règlements FFE officiels
const reglementsFrance = [
  {
    title: "Règlement Général 2026",
    description: "Version avec rectificatif applicable au 05.01.2026",
    url: "https://www.ffe.com/media/13278/download",
    category: "Règlement Général"
  },
  {
    title: "Règlement Endurance 2026",
    description: "Applicable au 01-09-2025",
    url: "https://www.ffe.com/media/13270/download",
    category: "Règlement Discipline"
  },
  {
    title: "Calendrier Championnats de France 2026",
    description: "Dates et lieux des championnats",
    url: "https://www.ffe.com/media/13184/download",
    category: "Championnats"
  },
  {
    title: "Championnat de France Master 2025",
    description: "Règlement du championnat Master",
    url: "https://www.ffe.com/media/7104/download",
    category: "Championnats"
  },
  {
    title: "Championnat de France Amateur 2025",
    description: "Avec rectificatif applicable",
    url: "https://www.ffe.com/media/11018/download",
    category: "Championnats"
  },
  {
    title: "Championnat de France Chevaux 7 ans",
    description: "Règlement spécifique jeunes chevaux",
    url: "https://www.ffe.com/media/16599/download",
    category: "Championnats"
  },
  {
    title: "Carte Suivi Vétérinaire Amateur et Pro",
    description: "Document obligatoire pour les épreuves",
    url: "https://www.ffe.com/media/7111/download",
    category: "Vétérinaire"
  },
  {
    title: "Carte Suivi Vétérinaire Club",
    description: "Document obligatoire pour les épreuves club",
    url: "https://www.ffe.com/media/7112/download",
    category: "Vétérinaire"
  },
  {
    title: "Critères Qualifications Internationales 2025",
    description: "Conditions pour les compétitions internationales",
    url: "https://www.ffe.com/media/10185/download",
    category: "Qualifications"
  },
  {
    title: "Saison Sportive Endurance 2025",
    description: "Document de référence haut niveau",
    url: "https://www.ffe.com/media/12476/download",
    category: "Haut Niveau"
  }
];

// Règlements FEI officiels
const reglementsFEI = [
  {
    title: "Règlement FEI Endurance",
    description: "Règles officielles de la Fédération Équestre Internationale",
    url: "https://inside.fei.org/fei/disc/endurance/rules",
    category: "Règlement FEI"
  },
  {
    title: "Anti-Doping Rules (EADCMRs 2025)",
    description: "Règles anti-dopage équines et humaines",
    url: "https://inside.fei.org/sites/default/files/EADCMRs-%20Final%20Draft%20effective%201%20January%202025%20-%20clean.pdf",
    category: "Anti-dopage"
  },
  {
    title: "Classement Mondial FEI",
    description: "Ranking list cavaliers et chevaux",
    url: "https://data.fei.org/Ranking/List.aspx",
    category: "Classements"
  },
  {
    title: "Calendrier & Résultats FEI",
    description: "Compétitions internationales",
    url: "https://data.fei.org/Calendar/Search.aspx",
    category: "Calendrier"
  },
  {
    title: "Rechercher un cavalier FEI",
    description: "Base de données des cavaliers",
    url: "https://data.fei.org/person/search.aspx",
    category: "Base de données"
  },
  {
    title: "Rechercher un cheval FEI",
    description: "Base de données des chevaux",
    url: "https://data.fei.org/Horse/Search.aspx",
    category: "Base de données"
  }
];

// Règlements SHF (Jeunes Chevaux)
const reglementsSHF = [
  {
    title: "Règlements SHF Élevage",
    description: "Règlements généraux de la Société Hippique Française",
    url: "https://www.shf.eu/fr/elevage/reglements/",
    category: "Règlement Général"
  },
  {
    title: "France Endurance",
    description: "Règlement spécifique endurance SHF",
    url: "https://www.shf.eu/fr/elevage/reglements/france-endurance.html",
    category: "Endurance"
  },
  {
    title: "Association Cheval Arabe",
    description: "Règlement pour les chevaux arabes",
    url: "https://www.shf.eu/fr/elevage/reglements/association-cheval-arabe.html",
    category: "Chevaux Arabes"
  },
  {
    title: "Documents Endurance SHF",
    description: "Informations et documents de valorisation",
    url: "https://www.shf.eu/fr/valorisation/informations-documents/endurance/",
    category: "Documents"
  }
];

// Résultats des courses Brious'O Folies (à compléter par l'association)
const resultatsAssociation = {
  2026: [],
  2025: [],
  2024: [],
  2023: []
};

const Endurance = () => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState("reglements");
  const [selectedYear, setSelectedYear] = useState("2024");

  // Grouper les règlements par catégorie
  const groupByCategory = (reglements) => {
    return reglements.reduce((acc, reg) => {
      if (!acc[reg.category]) acc[reg.category] = [];
      acc[reg.category].push(reg);
      return acc;
    }, {});
  };

  const franceGrouped = groupByCategory(reglementsFrance);
  const feiGrouped = groupByCategory(reglementsFEI);
  const shfGrouped = groupByCategory(reglementsSHF);

  return (
    <div className="min-h-screen bg-gradient-to-b from-zinc-50 to-white" data-testid="endurance-page">
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-fuchsia-900 via-fuchsia-800 to-pink-900 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }} />
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-white/10 rounded-full px-4 py-2 mb-6">
              <Trophy className="h-5 w-5 text-lime-400" />
              <span className="text-white/90">Endurance Équestre</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              {t('endurance.title')}
            </h1>
            <p className="text-xl text-white/80 max-w-2xl mx-auto">
              {t('endurance.subtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="max-w-6xl mx-auto">
            <TabsList className="grid w-full grid-cols-2 mb-8">
              <TabsTrigger value="reglements" className="flex items-center gap-2">
                <FileText className="h-4 w-4" />
                {t('endurance.regulations')}
              </TabsTrigger>
              <TabsTrigger value="resultats" className="flex items-center gap-2">
                <Trophy className="h-4 w-4" />
                {t('endurance.results')}
              </TabsTrigger>
            </TabsList>

            {/* Règlements Tab */}
            <TabsContent value="reglements">
              <div className="grid md:grid-cols-2 gap-8">
                {/* Règlements France FFE */}
                <div>
                  <div className="flex items-center gap-3 mb-6">
                    <div className="h-12 w-12 rounded-xl bg-blue-100 flex items-center justify-center">
                      <Flag className="h-6 w-6 text-blue-600" />
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold text-zinc-800">{t('endurance.france')}</h2>
                      <p className="text-zinc-500">Fédération Française d'Équitation</p>
                    </div>
                  </div>
                  
                  <div className="space-y-3">
                    {Object.entries(franceGrouped).map(([category, regs]) => (
                      <div key={category} className="mb-4">
                        <h3 className="text-sm font-semibold text-zinc-500 uppercase tracking-wide mb-2">{category}</h3>
                        {regs.map((reg, index) => (
                          <Card key={index} className="mb-2 hover:shadow-md transition-shadow border-l-4 border-l-blue-500">
                            <CardContent className="p-3">
                              <div className="flex justify-between items-center">
                                <div className="flex-1">
                                  <h4 className="font-medium text-zinc-800 text-sm">{reg.title}</h4>
                                  <p className="text-xs text-zinc-500">{reg.description}</p>
                                </div>
                                <a 
                                  href={reg.url} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="ml-3"
                                >
                                  <Button variant="outline" size="sm" className="h-8 w-8 p-0">
                                    <Download className="h-4 w-4" />
                                  </Button>
                                </a>
                              </div>
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Règlements FEI International */}
                <div>
                  <div className="flex items-center gap-3 mb-6">
                    <div className="h-12 w-12 rounded-xl bg-fuchsia-100 flex items-center justify-center">
                      <Globe className="h-6 w-6 text-fuchsia-600" />
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold text-zinc-800">{t('endurance.international')}</h2>
                      <p className="text-zinc-500">Fédération Équestre Internationale</p>
                    </div>
                  </div>
                  
                  <div className="space-y-3">
                    {Object.entries(feiGrouped).map(([category, regs]) => (
                      <div key={category} className="mb-4">
                        <h3 className="text-sm font-semibold text-zinc-500 uppercase tracking-wide mb-2">{category}</h3>
                        {regs.map((reg, index) => (
                          <Card key={index} className="mb-2 hover:shadow-md transition-shadow border-l-4 border-l-fuchsia-500">
                            <CardContent className="p-3">
                              <div className="flex justify-between items-center">
                                <div className="flex-1">
                                  <h4 className="font-medium text-zinc-800 text-sm">{reg.title}</h4>
                                  <p className="text-xs text-zinc-500">{reg.description}</p>
                                </div>
                                <a 
                                  href={reg.url} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="ml-3"
                                >
                                  <Button variant="outline" size="sm" className="h-8 w-8 p-0">
                                    <ExternalLink className="h-4 w-4" />
                                  </Button>
                                </a>
                              </div>
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Section SHF */}
              <div className="mt-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="h-12 w-12 rounded-xl bg-lime-100 flex items-center justify-center">
                    <Award className="h-6 w-6 text-lime-600" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-zinc-800">{t('endurance.youngHorses')}</h2>
                    <p className="text-zinc-500">Société Hippique Française</p>
                  </div>
                </div>
                
                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {Object.entries(shfGrouped).map(([category, regs]) => (
                    <div key={category}>
                      {regs.map((reg, index) => (
                        <Card key={index} className="hover:shadow-md transition-shadow border-l-4 border-l-lime-500">
                          <CardContent className="p-3">
                            <div className="flex justify-between items-center">
                              <div className="flex-1">
                                <h4 className="font-medium text-zinc-800 text-sm">{reg.title}</h4>
                                <p className="text-xs text-zinc-500">{reg.description}</p>
                              </div>
                              <a 
                                href={reg.url} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="ml-3"
                              >
                                <Button variant="outline" size="sm" className="h-8 w-8 p-0">
                                  <ExternalLink className="h-4 w-4" />
                                </Button>
                              </a>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  ))}
                </div>
              </div>

              {/* Liens utiles */}
              <div className="mt-12 p-6 bg-gradient-to-r from-zinc-100 to-zinc-50 rounded-2xl">
                <h3 className="text-lg font-semibold text-zinc-800 mb-4">{t('endurance.usefulLinks')}</h3>
                <div className="grid md:grid-cols-4 gap-4">
                  <a href="https://www.ffe.com/pratiquer/disciplines/endurance" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-zinc-600 hover:text-blue-600 transition-colors">
                    <ExternalLink className="h-4 w-4" />
                    FFE Endurance
                  </a>
                  <a href="https://ffecompet.ffe.com" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-zinc-600 hover:text-blue-600 transition-colors">
                    <ExternalLink className="h-4 w-4" />
                    FFE Compet
                  </a>
                  <a href="https://inside.fei.org/fei/disc/endurance" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-zinc-600 hover:text-fuchsia-600 transition-colors">
                    <ExternalLink className="h-4 w-4" />
                    FEI Endurance
                  </a>
                  <a href="https://www.shf.eu" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-zinc-600 hover:text-lime-600 transition-colors">
                    <ExternalLink className="h-4 w-4" />
                    SHF (Jeunes Chevaux)
                  </a>
                </div>
              </div>
            </TabsContent>

            {/* Résultats Tab - Courses Brious'O Folies */}
            <TabsContent value="resultats">
              {/* Year selector */}
              <div className="flex flex-wrap gap-2 mb-8 justify-center">
                {["2026", "2025", "2024", "2023"].map((year) => (
                  <Button
                    key={year}
                    variant={selectedYear === year ? "default" : "outline"}
                    onClick={() => setSelectedYear(year)}
                    className={selectedYear === year ? "bg-fuchsia-600 hover:bg-fuchsia-700" : ""}
                  >
                    {year}
                  </Button>
                ))}
              </div>

              {/* Info message */}
              <Card className="mb-8 border-lime-200 bg-lime-50">
                <CardContent className="p-6 text-center">
                  <Trophy className="h-12 w-12 text-lime-600 mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-zinc-800 mb-2">Résultats des courses Brious'O Folies</h3>
                  <p className="text-zinc-600 mb-4">
                    Cette page présentera les résultats détaillés des épreuves d'endurance et de randonnée organisées par notre association.
                  </p>
                  <div className="flex flex-wrap justify-center gap-4 text-sm text-zinc-500">
                    <span className="flex items-center gap-1"><Medal className="h-4 w-4" /> Classements par épreuve</span>
                    <span className="flex items-center gap-1"><Users className="h-4 w-4" /> Liste des participants</span>
                    <span className="flex items-center gap-1"><Clock className="h-4 w-4" /> Temps et chronos</span>
                  </div>
                </CardContent>
              </Card>

              {/* Placeholder for results */}
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {/* Example event card structure - to be filled with real data */}
                <Card className="border-dashed border-2 border-zinc-300 bg-zinc-50/50">
                  <CardContent className="p-6 text-center">
                    <div className="h-16 w-16 rounded-full bg-zinc-200 mx-auto mb-4 flex items-center justify-center">
                      <Calendar className="h-8 w-8 text-zinc-400" />
                    </div>
                    <h4 className="font-semibold text-zinc-500 mb-2">Endurance 40km</h4>
                    <p className="text-sm text-zinc-400">Résultats à venir</p>
                  </CardContent>
                </Card>
                
                <Card className="border-dashed border-2 border-zinc-300 bg-zinc-50/50">
                  <CardContent className="p-6 text-center">
                    <div className="h-16 w-16 rounded-full bg-zinc-200 mx-auto mb-4 flex items-center justify-center">
                      <Calendar className="h-8 w-8 text-zinc-400" />
                    </div>
                    <h4 className="font-semibold text-zinc-500 mb-2">Randonnée 20km</h4>
                    <p className="text-sm text-zinc-400">Résultats à venir</p>
                  </CardContent>
                </Card>
                
                <Card className="border-dashed border-2 border-zinc-300 bg-zinc-50/50">
                  <CardContent className="p-6 text-center">
                    <div className="h-16 w-16 rounded-full bg-zinc-200 mx-auto mb-4 flex items-center justify-center">
                      <Calendar className="h-8 w-8 text-zinc-400" />
                    </div>
                    <h4 className="font-semibold text-zinc-500 mb-2">Balade Découverte</h4>
                    <p className="text-sm text-zinc-400">Résultats à venir</p>
                  </CardContent>
                </Card>
              </div>

              <div className="mt-8 text-center">
                <p className="text-zinc-500 text-sm">
                  Les résultats seront publiés après chaque événement organisé par l'association.
                </p>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-fuchsia-50 to-pink-50">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-2xl font-bold text-zinc-800 mb-4">Envie de participer ?</h2>
          <p className="text-zinc-600 mb-6 max-w-xl mx-auto">
            Découvrez nos prochains événements d'endurance et inscrivez-vous à nos courses.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/evenements">
              <Button className="bg-fuchsia-600 hover:bg-fuchsia-700">
                <Calendar className="h-4 w-4 mr-2" />
                Voir nos événements
              </Button>
            </Link>
            <Link to="/actualites">
              <Button variant="outline">
                <ArrowRight className="h-4 w-4 mr-2" />
                Retour aux actualités
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Endurance;
